import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import EligibleStudentPicker from '@/pages/extras/EligibleStudentPicker';

/**
 * Деталка доп. занятия в ЛК учителя. Показывает шапку, состав учеников,
 * даёт «Провести», «Отменить», «Добавить ученика», «Убрать ученика»,
 * «Редактировать» (для planned). Удаления нет — это админская привилегия
 * (требует extra.schedule:delete).
 *
 * Внутри — те же эндпойнты `/api/extra-schedule/...`. Бэк сам проверяет
 * `requireEntryAccess` и пропустит учителя, если карточка занятия его.
 */
const STATUS_LABELS = {
  planned:   { label: 'Запланировано', cls: 'pay-st pay-st--draft' },
  conducted: { label: 'Проведено',     cls: 'pay-st pay-st--ok' },
  canceled:  { label: 'Отменено',      cls: 'pay-st pay-st--mute' },
};

function shortTime(t) { return (t || '').slice(0, 5); }
function fmtDateTime(iso) {
  if (!iso) return '—';
  return iso.replace('T', ' ').slice(0, 16);
}
function fmtDateRu(iso) {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
}

export default function TeacherExtraEntryModal({ entryId, onClose }) {
  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);

  const [addOpen, setAddOpen]         = useState(false);
  const [conductOpen, setConductOpen] = useState(false);
  const [cancelOpen, setCancelOpen]   = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // Edit-форма (для planned): date / time / room / comment
  const [editOpen, setEditOpen]     = useState(false);
  const [editDate, setEditDate]     = useState('');
  const [editTimeId, setEditTimeId] = useState('');
  const [editRoomId, setEditRoomId] = useState('');
  const [editComment, setEditComment] = useState('');
  const [times, setTimes] = useState([]);
  const [rooms, setRooms] = useState([]);

  useEffect(() => {
    if (!editOpen) return;
    api.get('/api/extra-lesson-times').then((r) => setTimes((r.rows || []).filter((x) => !x.archive))).catch(() => {});
    api.get('/api/rooms').then((r) => setRooms(r.rows || [])).catch(() => {});
  }, [editOpen]);

  // Несохранённые данные: открыта форма правки шапки или введена причина
  // отмены. В этих состояниях случайный клик мимо окна / Escape не должен
  // молча закрывать модалку — сначала спросим.
  const hasUnsavedChanges = () =>
    editOpen || (cancelOpen && cancelReason.trim() !== '');
  const attemptClose = () => {
    if (busy) return;
    if (hasUnsavedChanges() && !window.confirm('Закрыть окно без сохранения данных?')) return;
    onClose();
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') attemptClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, busy, editOpen, cancelOpen, cancelReason]);

  const reload = () => {
    setError(null);
    api.get('/api/extra-schedule/' + entryId)
      .then(setData)
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'));
  };
  useEffect(reload, [entryId]);

  const act = async (fn) => {
    setBusy(true); setError(null);
    try { await fn(); reload(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Не удалось'); }
    finally { setBusy(false); }
  };

  const onAddStudent    = (s)   => { act(() => api.post(`/api/extra-schedule/${entryId}/students`, { studentId: s.id })); setAddOpen(false); };
  const onRemoveStudent = (sid) => { act(() => api.delete(`/api/extra-schedule/${entryId}/students/${sid}`)); };

  const beginEdit = () => {
    if (!data?.entry) return;
    setEditDate(data.entry.date || '');
    setEditTimeId(data.entry.timeId ? String(data.entry.timeId) : '');
    setEditRoomId(data.entry.roomId ? String(data.entry.roomId) : '__none__');
    setEditComment(data.entry.comment || '');
    setEditOpen(true);
  };
  const onSaveEdit = () => {
    const body = {};
    if (editDate)             body.date   = editDate;
    if (editTimeId)           body.timeId = parseInt(editTimeId, 10);
    if (editRoomId !== '')    body.roomId = editRoomId === '__none__' ? null : parseInt(editRoomId, 10);
    body.comment = editComment;
    act(() => api.put('/api/extra-schedule/' + entryId, body));
    setEditOpen(false);
  };

  const onConduct = () => { act(() => api.post(`/api/extra-schedule/${entryId}/conduct`, {})); setConductOpen(false); };
  const onCancel  = () => { act(() => api.post(`/api/extra-schedule/${entryId}/cancel`, { reason: cancelReason || '' })); setCancelOpen(false); setCancelReason(''); };

  const e        = data?.entry;
  const students = data?.students || [];
  const isPlanned   = e?.status === 'planned';
  const isConducted = e?.status === 'conducted';
  const isCanceled  = e?.status === 'canceled';
  const isIndividual = e?.lesson?.type === 'individual';
  const statusInfo  = e ? (STATUS_LABELS[e.status] || { label: e.status, cls: 'pay-st' }) : null;
  // Индивидуальное занятие в planned-режиме — допускает 0 или 1 ученика.
  // Кнопку «+ добавить» прячем, если уже один.
  const canAddStudent = isPlanned && (!isIndividual || students.length === 0);

  return (
    <div className="sd-overlay" onClick={(ev) => { if (ev.target.classList.contains('sd-overlay')) attemptClose(); }}>
      <div className="sd-modal sd-modal--medium" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>
            {e?.lesson?.subject?.name || 'Доп. занятие'}{' '}
            {e && <span className="muted" style={{ fontWeight: 400, fontSize: 13 }}>· {fmtDateRu(e.date)} {shortTime(e.slotStart)}–{shortTime(e.slotEnd)}</span>}
          </h2>
          <button className="sd-close" onClick={onClose} disabled={busy}><Icon name="x" size={14} /></button>
        </div>

        {/* .sd-edit__body — это 2-колоночный grid; всё содержимое
            детальной формы у нас в одну колонку, поэтому каждый блок
            заворачиваем в .sd-edit__field--full. */}
        <div className="sd-edit__body">
          {error && <div className="sd-edit__error sd-edit__field--full">{error}</div>}
          {!data && !error && <div className="muted sd-edit__field--full">Загрузка…</div>}

          {e && (
            <>
              {/* Шапка занятия — сводка в одну строку */}
              <div className="sd-edit__field--full"
                   style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'baseline' }}>
                <div>
                  <div className="sd-edit__label">Статус</div>
                  <div><span className={statusInfo.cls}>{statusInfo.label}</span></div>
                </div>
                <div>
                  <div className="sd-edit__label">Тип</div>
                  <div>{isIndividual ? 'Индивидуальное' : 'Групповое'}</div>
                </div>
                <div>
                  <div className="sd-edit__label">Кабинет</div>
                  <div>{e.roomName || '—'}</div>
                </div>
                {e.conductedAt && (
                  <div>
                    <div className="sd-edit__label">Проведено</div>
                    <div className="muted">{fmtDateTime(e.conductedAt)}</div>
                  </div>
                )}
                {e.comment && (
                  <div style={{ flex: '1 1 100%' }}>
                    <div className="sd-edit__label">Комментарий</div>
                    <div>{e.comment}</div>
                  </div>
                )}
                {e.canceledAt && (
                  <div style={{ flex: '1 1 100%' }}>
                    <div className="sd-edit__label">Отменено</div>
                    <div className="muted">
                      {fmtDateTime(e.canceledAt)}
                      {e.cancelReason && <span> · {e.cancelReason}</span>}
                    </div>
                  </div>
                )}
              </div>

              {/* Действия по статусу */}
              {isPlanned && (
                <div className="sd-edit__field--full"
                     style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button className="btn btn--primary btn--sm" disabled={busy || students.length === 0}
                          onClick={() => setConductOpen(true)}
                          title={students.length === 0 ? 'Сначала добавьте учеников' : 'Отметить занятие проведённым'}>
                    <Icon name="check" size={12} /> Провести
                  </button>
                  <button className="btn btn--sm" disabled={busy} onClick={beginEdit}>
                    <Icon name="edit" size={12} /> Редактировать
                  </button>
                  <button className="btn btn--ghost btn--sm" disabled={busy}
                          onClick={() => setCancelOpen(true)} style={{ color: 'var(--red-fg)' }}>
                    <Icon name="x" size={12} /> Отменить
                  </button>
                </div>
              )}
              {isConducted && (
                <div className="sd-edit__field--full" style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn--ghost btn--sm" disabled={busy}
                          onClick={() => setCancelOpen(true)} style={{ color: 'var(--red-fg)' }}
                          title="Отмена проведённого занятия сторнирует начисления">
                    <Icon name="x" size={12} /> Отменить
                  </button>
                </div>
              )}

              {/* Inline-формы подтверждений */}
              {conductOpen && (
                <div className="sd-edit__field sd-edit__field--full"
                     style={{ background: 'var(--panel-2)', padding: 12, borderRadius: 8 }}>
                  <div className="sd-edit__hint">
                    Подтвердите: занятие проведено? Это создаст начисления учителю и спишет
                    оплату с учеников.
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button className="btn btn--primary btn--sm" onClick={onConduct} disabled={busy}>Да, провести</button>
                    <button className="btn btn--sm" onClick={() => setConductOpen(false)} disabled={busy}>Отмена</button>
                  </div>
                </div>
              )}
              {cancelOpen && (
                <div className="sd-edit__field sd-edit__field--full"
                     style={{ background: 'var(--panel-2)', padding: 12, borderRadius: 8 }}>
                  <div className="sd-edit__label">Причина отмены (необязательно)</div>
                  <input className="sd-edit__input" value={cancelReason}
                         onChange={(ev) => setCancelReason(ev.target.value)}
                         placeholder="например: болезнь учителя" />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button className="btn btn--primary btn--sm" onClick={onCancel} disabled={busy}
                            style={{ background: 'var(--red-fg)' }}>
                      {isConducted ? 'Отменить (сторно)' : 'Отменить'}
                    </button>
                    <button className="btn btn--sm" onClick={() => { setCancelOpen(false); setCancelReason(''); }} disabled={busy}>
                      Закрыть
                    </button>
                  </div>
                </div>
              )}

              {/* Редактирование шапки */}
              {editOpen && (
                <div className="sd-edit__field sd-edit__field--full"
                     style={{ background: 'var(--panel-2)', padding: 12, borderRadius: 8 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div>
                      <div className="sd-edit__label">Дата</div>
                      <input type="date" className="sd-edit__input" value={editDate} onChange={(ev) => setEditDate(ev.target.value)} />
                    </div>
                    <div>
                      <div className="sd-edit__label">Слот</div>
                      <select className="sd-edit__select" value={editTimeId} onChange={(ev) => setEditTimeId(ev.target.value)}>
                        <option value="">— не менять —</option>
                        {times.map((t) => (
                          <option key={t.id} value={t.id}>
                            {(t.timeStart || '').slice(0, 5)} — {(t.timeEnd || '').slice(0, 5)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <div className="sd-edit__label">Кабинет</div>
                      <select className="sd-edit__select" value={editRoomId} onChange={(ev) => setEditRoomId(ev.target.value)}>
                        <option value="__none__">— не указан —</option>
                        {rooms.map((r) => (<option key={r.id} value={r.id}>{r.name}</option>))}
                      </select>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <div className="sd-edit__label">Комментарий</div>
                      <input className="sd-edit__input" value={editComment} onChange={(ev) => setEditComment(ev.target.value)} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <button className="btn btn--primary btn--sm" onClick={onSaveEdit} disabled={busy}>Сохранить</button>
                    <button className="btn btn--sm" onClick={() => setEditOpen(false)} disabled={busy}>Отмена</button>
                  </div>
                </div>
              )}

              {/* Состав учеников */}
              <div className="sd-edit__field sd-edit__field--full">
                <div className="sd-edit__label">
                  Ученики {students.length > 0 && <span className="muted">· {students.length}</span>}
                </div>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>ФИО</th>
                        <th style={{ width: 180 }}>Договор</th>
                        {isPlanned && <th style={{ width: 50 }} />}
                      </tr>
                    </thead>
                    <tbody>
                      {students.length === 0 ? (
                        <tr>
                          <td colSpan={isPlanned ? 3 : 2} className="muted" style={{ textAlign: 'center', padding: 16 }}>
                            Учеников пока нет. {isPlanned && 'Нажмите «+ добавить ученика».'}
                          </td>
                        </tr>
                      ) : students.map((s) => (
                        <tr key={s.studentId}>
                          <td>{s.studentName || [s.familyName, s.firstName, s.midName].filter(Boolean).join(' ')}</td>
                          <td className="muted">
                            {s.agreement ? <>№ {s.agreement.number}</> : '—'}
                          </td>
                          {isPlanned && (
                            <td>
                              <button className="btn btn--ghost btn--sm" style={{ color: 'var(--red-fg)' }}
                                      title="Убрать из занятия"
                                      onClick={() => onRemoveStudent(s.studentId)} disabled={busy}>
                                <Icon name="x" size={12} />
                              </button>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {canAddStudent && (
                <div className="sd-edit__field sd-edit__field--full">
                  {!addOpen ? (
                    <button className="btn btn--sm" onClick={() => setAddOpen(true)} disabled={busy}>
                      <Icon name="plus" size={12} /> добавить ученика
                    </button>
                  ) : (
                    <div style={{ background: 'var(--panel-2)', padding: 10, borderRadius: 8 }}>
                      <EligibleStudentPicker
                        date={e.date}
                        excludeIds={students.map((s) => s.studentId)}
                        onPick={onAddStudent}
                      />
                      <div style={{ marginTop: 8 }}>
                        <button className="btn btn--sm" onClick={() => setAddOpen(false)}>Закрыть</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="sd-edit__foot">
          <div className="sd-edit__foot-spacer" />
          <button className="btn btn--sm" onClick={onClose} disabled={busy}>Закрыть</button>
        </div>
      </div>
    </div>
  );
}
